package dev.lingvo.site.pages.app.projects.slug.screenshots

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import dev.lingvo.shared.PutScreenshotDesignRequest
import dev.lingvo.shared.ScreenshotDesignDto
import dev.lingvo.shared.ScreenshotDesignEnvelope
import dev.lingvo.shared.StorePlatform
import dev.lingvo.shared.screenshots.doc.DecodeResult
import dev.lingvo.shared.screenshots.doc.DesignCodec
import dev.lingvo.shared.screenshots.doc.ScreenshotDesign
import dev.lingvo.shared.screenshots.doc.StoreDeviceClassRule
import dev.lingvo.site.api.Session
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.api.ApiException
import kotlinx.browser.document as browserDocument
import kotlinx.browser.window
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.intOrNull
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put
import org.w3c.dom.Element
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.events.EventListener
import org.w3c.dom.events.MouseEvent
import kotlin.js.Date
import kotlin.random.Random
import dev.lingvo.site.i18n.t

/** Owns one authority-bound design session; callers flush before switching the slot or device. */
class DesignAutosaveController internal constructor(
    private val slug: String,
    private val platform: StorePlatform,
    initial: ScreenshotDesign?,
    baseVersion: Int,
    writable: Boolean,
    private val save: (PutScreenshotDesignRequest, (ScreenshotDesignDto) -> Unit, (Throwable) -> Unit) -> Unit,
    private val loadLatest: ((ScreenshotDesignDto) -> Unit, (Throwable) -> Unit) -> Unit,
) {
    private val writerId = "${Date.now()}-${Random.nextLong()}"
    private var lastWrittenDraft: String? = null
    private val ownerId = Session.user?.id
    private val ownerMatches get() = ownerId != null && Session.user?.id == ownerId
    private var snapshot by mutableStateOf(DesignAutosaveSnapshot(initial, baseVersion))
    private var draft: DesignDraft? by mutableStateOf(readDraft())
    private var timer: Int? = null
    private var mounted = true
    private var loadingLatest = false
    private var requestNumber = 0
    private val debounce = DesignSaveScheduler(writable,
        needsSave = { mounted && state in setOf(DesignSaveState.DIRTY, DesignSaveState.OFFLINE) && snapshot.savingRevision == null },
        afterIdle = { action -> timer = window.setTimeout(action, 1500) },
        cancelIdle = { cancelTimer() }, save = { flush() })
    internal var writable: Boolean
        get() = debounce.writable
        set(value) { debounce.writable = value }
    private val key get() = "lingvo:studio-draft:$slug:${platform.id}"

    /** The newest local document, including edits committed while a save is in flight. */
    val document: ScreenshotDesign? get() = snapshot.document
    /** Identifies the exact server base used by template application and history restoration. */
    val version: Int get() = snapshot.baseVersion
    /** Acknowledged restores never overwrite edits made while the restore request was in flight. */
    fun adoptRestored(dto: ScreenshotDesignDto, base: Int, original: ScreenshotDesign?): Boolean {
        if (!mounted || state != DesignSaveState.CLEAN || version != base || document != original) return false
        require(dto.version == base + 1) { tr("app.projects.slug.screenshots.design_autosave.the_restored_version_could_not_be", "The restored version could not be verified. Reload the design.") }
        snapshot = DesignAutosaveSnapshot(decode(dto), dto.version)
        issues = emptyList()
        return true
    }
    /** Template use is an explicit new version even when it reapplies the current layout. */
    fun applyTemplate(value: ScreenshotDesign): Boolean {
        if (!mounted || !writable) return false
        val validated = checked(value)
        require(validated.platform == platform)
        snapshot = snapshot.copy(document = validated, revision = snapshot.revision + 1,
            state = if (state == DesignSaveState.CONFLICT) state else DesignSaveState.DIRTY)
        issues = emptyList()
        persist()
        flush()
        return true
    }
    /** Observable progress; CLEAN is the only state that permits an unguarded unload. */
    val state: DesignSaveState get() = snapshot.state
    /** Timestamp of a validated draft offered for explicit restoration. */
    val draftTime: String? get() = draft?.time?.takeIf { ownerMatches }
    /** Latest guarded server read, required before either conflict choice can proceed. */
    val conflict: ScreenshotDesignDto? get() = snapshot.conflict
    /** Repair messages use human-readable paths, never raw server traces. */
    var issues: List<String> by mutableStateOf(emptyList())
        private set
    /** Compact toolbar status with an explicit retry affordance for failures. */
    val statusLabel: String get() = when (state) {
        DesignSaveState.CLEAN -> tr("app.projects.slug.screenshots.design_autosave.saved", "Saved")
        DesignSaveState.DIRTY -> tr("app.projects.slug.screenshots.design_autosave.unsaved_changes", "Unsaved changes")
        DesignSaveState.SAVING -> tr("app.projects.slug.screenshots.design_autosave.saving", "Saving…")
        DesignSaveState.CONFLICT -> tr("app.projects.slug.screenshots.design_autosave.another_version_was_saved", "Another version was saved")
        DesignSaveState.OFFLINE -> tr("app.projects.slug.screenshots.design_autosave.offline_kept_in_this_tab", "Offline, kept in this tab")
        DesignSaveState.FAILED -> tr("app.projects.slug.screenshots.design_autosave.couldnt_save_retry", "Couldn't save. Retry")
    }

    /** Commits a validated edit and restarts the 1,500 ms idle window. */
    fun edit(document: ScreenshotDesign) {
        if (!mounted || !writable || !ownerMatches) return
        val value = runCatching { checked(document) }.getOrElse {
            issues = listOf(it.message?.lineSequence()?.map { line ->
                val parts = line.split(":", limit = 2)
                if (parts.size == 2) trf("screenshots.design_autosave.check_path", "Check {path} before saving.",
                    "path" to designPath(parts[0])) else tr("app.projects.slug.screenshots.design_autosave.check_this_design_before_saving", "Check this design before saving.")
            }?.joinToString("\n") ?: tr("app.projects.slug.screenshots.design_autosave.check_this_design_before_saving", "Check this design before saving."))
            return
        }
        if (value.platform != platform || value == this.document) return
        snapshot = snapshot.edit(value)
        issues = emptyList()
        persist()
        schedule()
    }

    /** Immediately attempts the latest draft without starting a second concurrent PUT. */
    fun flush() {
        cancelTimer()
        if (!mounted || !writable || !ownerMatches) return
        val next = snapshot.begin(window.navigator.onLine)
        if (next == snapshot) return
        snapshot = next
        persist()
        if (state != DesignSaveState.SAVING) return
        val sent = requireNotNull(document)
        val requestId = ++requestNumber
        val request = PutScreenshotDesignRequest(snapshot.baseVersion,
            ScreenshotDesignEnvelope(sent.schemaVersion, Json.parseToJsonElement(DesignCodec.encode(sent)) as JsonObject))
        try {
            save(request, { dto ->
                if (mounted && requestId == requestNumber && snapshot.savingRevision != null) runCatching {
                    val canonical = decode(dto)
                    require(dto.version == request.baseVersion + 1) { tr("app.projects.slug.screenshots.design_autosave.the_saved_version_could_not_be", "The saved version could not be verified. Retry.") }
                    snapshot = snapshot.saved(dto, canonical, sent)
                    issues = emptyList()
                    if (state == DesignSaveState.CLEAN) {
                        draft = null
                        removeStoredDraft(lastWrittenDraft)
                        lastWrittenDraft = null
                    } else { persist(); schedule() }
                }.onFailure(::saveFailed)
            }, { if (mounted && requestId == requestNumber && snapshot.savingRevision != null) saveFailed(it) })
        } catch (error: Throwable) { if (mounted && requestId == requestNumber && snapshot.savingRevision != null) saveFailed(error) }
    }

    /** A restore refusal enters the same guarded reconciliation path as a stale autosave. */
    fun reconcileConflict(error: ApiException) {
        if (!mounted || !writable || error.status != 409 || error.code != "design_version_conflict") return
        snapshot = snapshot.failed(false, true)
        issues = designSaveIssues(error)
        persist()
        fetchLatest()
    }

    /** Retries a failed write or the conflict's guarded latest-version read. */
    fun retry() {
        if (state == DesignSaveState.CONFLICT) fetchLatest() else flush()
    }

    /** Adopts the fetched version, retaining this unsaved document as an explicit local recovery offer. */
    fun loadTheirs() {
        val theirs = conflict ?: return
        runCatching {
            val value = decode(theirs)
            persist()
            draft = readDraft()
            snapshot = snapshot.loadTheirs(value)
            issues = emptyList()
        }.onFailure { issues = listOf(it.message ?: tr("app.projects.slug.screenshots.design_autosave.their_design_could_not_be_read", "Their design could not be read. Retry.")) }
    }

    /** Rebases this document onto the fetched version and appends through the normal guarded PUT. */
    fun keepMine() {
        if (!writable || !ownerMatches || conflict == null) return
        snapshot = snapshot.keepMine()
        issues = emptyList()
        persist()
        flush()
    }

    /** Restores the draft's original base token, so stale recovery still requires conflict resolution. */
    fun restoreDraft() {
        if (!writable || !ownerMatches || snapshot.savingRevision != null) return
        val recovered = draft ?: return
        snapshot = DesignAutosaveSnapshot(recovered.document, recovered.baseVersion, DesignSaveState.DIRTY,
            revision = snapshot.revision + 1)
        draft = null
        issues = emptyList()
        persist()
        schedule()
    }

    /** Dismisses the offered recovery copy, preserving a newer draft written by another tab. */
    fun discardDraft() {
        val offered = draft?.serialized
        draft = null
        removeStoredDraft(offered)
    }

    private fun removeStoredDraft(expected: String?) {
        if (expected == null || !ownerMatches) return
        runCatching {
            if (window.localStorage.getItem(key) == expected) window.localStorage.removeItem(key)
        }
    }

    internal fun attach(): () -> Unit {
        val beforeUnload = EventListener { event ->
            if (state != DesignSaveState.CLEAN) {
                persist()
                event.preventDefault()
                event.asDynamic().returnValue = ""
            }
        }
        val visibility = EventListener { if (browserDocument.asDynamic().hidden as? Boolean == true) { persist(); flush() } }
        val online = EventListener { retry() }
        val offline = EventListener {
            if (state != DesignSaveState.CLEAN && snapshot.savingRevision == null && state != DesignSaveState.CONFLICT) {
                snapshot = snapshot.failed(true, false)
                persist()
            }
        }
        val routeGuard = EventListener { raw ->
            val event = raw as? MouseEvent ?: return@EventListener
            if (state == DesignSaveState.CLEAN || event.button.toInt() != 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return@EventListener
            val anchor = nearestAnchor(event.target) ?: return@EventListener
            if (anchor.hasAttribute("download") || anchor.target == "_blank" || anchor.href.isBlank() ||
                anchor.href == window.location.href || anchor.href.startsWith("javascript:")) return@EventListener
            if (!window.confirm(tr("app.projects.slug.screenshots.design_autosave.leave_the_design_studio_unsaved_changes", "Leave the design studio? Unsaved changes are still waiting to be saved."))) {
                event.preventDefault()
                event.stopImmediatePropagation()
            } else { persist(); flush() }
        }
        val historyGuard = EventListener { if (state != DesignSaveState.CLEAN) { persist(); flush() } }
        window.addEventListener("beforeunload", beforeUnload)
        browserDocument.addEventListener("visibilitychange", visibility)
        browserDocument.addEventListener("click", routeGuard, true)
        window.addEventListener("online", online)
        window.addEventListener("offline", offline)
        window.addEventListener("popstate", historyGuard)
        return {
            persist()
            flush()
            mounted = false
            cancelTimer()
            window.removeEventListener("beforeunload", beforeUnload)
            browserDocument.removeEventListener("visibilitychange", visibility)
            browserDocument.removeEventListener("click", routeGuard, true)
            window.removeEventListener("online", online)
            window.removeEventListener("offline", offline)
            window.removeEventListener("popstate", historyGuard)
        }
    }

    private fun schedule() {
        debounce.restart()
    }
    private fun cancelTimer() { timer?.let(window::clearTimeout); timer = null }
    private fun saveFailed(error: Throwable) {
        val conflict = error is ApiException && error.status == 409 && error.code == "design_version_conflict"
        snapshot = snapshot.failed(!window.navigator.onLine, conflict)
        issues = designSaveIssues(error)
        persist()
        if (conflict) fetchLatest()
    }
    private fun fetchLatest() {
        if (!mounted || loadingLatest) return
        loadingLatest = true
        try {
            loadLatest({ dto ->
                if (mounted) {
                    loadingLatest = false
                    runCatching { decode(dto); snapshot = snapshot.copy(conflict = dto) }
                        .onFailure { issues = listOf(it.message ?: tr("app.projects.slug.screenshots.design_autosave.their_design_could_not_be_read", "Their design could not be read. Retry.")) }
                }
            }, { if (mounted) { loadingLatest = false; issues = designSaveIssues(it) } })
        } catch (error: Throwable) { loadingLatest = false; issues = designSaveIssues(error) }
    }
    private fun decode(dto: ScreenshotDesignDto): ScreenshotDesign {
        require(dto.platform == platform && dto.schemaVersion > 0) { tr("app.projects.slug.screenshots.design_autosave.this_design_belongs_to_another_store", "This design belongs to another store.") }
        val result = DesignCodec.decodeElement(dto.document, StoreDeviceClassRule)
        require(result is DecodeResult.Ok && result.design.platform == platform && result.design.schemaVersion == dto.schemaVersion) {
            tr("app.projects.slug.screenshots.design_autosave.this_design_could_not_be_read", "This design could not be read safely. Retry or reload the studio.")
        }
        return result.design
    }
    private fun persist() {
        if (state == DesignSaveState.CLEAN || !ownerMatches) return
        val value = document ?: return
        runCatching {
            val serialized = buildJsonObject {
                put("writerId", writerId)
                put("ownerId", ownerId)
                put("time", Date().toISOString())
                put("baseVersion", snapshot.baseVersion)
                put("document", Json.parseToJsonElement(DesignCodec.encode(value)))
            }.toString()
            window.localStorage.setItem(key, serialized)
            lastWrittenDraft = serialized
        }
    }
    private fun readDraft(): DesignDraft? = runCatching {
        if (!ownerMatches) return@runCatching null
        val raw = window.localStorage.getItem(key) ?: return@runCatching null
        require(raw.length <= 300_000)
        val root = Json.parseToJsonElement(raw) as JsonObject
        if (root["ownerId"]?.jsonPrimitive?.content != ownerId) return@runCatching null
        val time = root["time"]?.jsonPrimitive?.content ?: return@runCatching null
        require(Date.parse(time).isFinite())
        val version = root["baseVersion"]?.jsonPrimitive?.intOrNull ?: return@runCatching null
        require(version >= 0)
        val result = DesignCodec.decode(root["document"].toString(), StoreDeviceClassRule)
        if (result is DecodeResult.Ok && result.design.platform == platform) {
            if (state == DesignSaveState.CLEAN && result.design == document && snapshot.baseVersion >= version) {
                removeStoredDraft(raw)
                null
            } else DesignDraft(time, version, result.design, raw)
        } else null
    }.getOrNull()
}

private data class DesignDraft(val time: String, val baseVersion: Int, val document: ScreenshotDesign, val serialized: String)

/** Remembers one design session; the caller keys this composable by the current project authority. */
@Composable
fun rememberDesignAutosave(
    slug: String,
    platform: StorePlatform,
    initial: ScreenshotDesign?,
    baseVersion: Int,
    canWrite: Boolean,
    save: (PutScreenshotDesignRequest, (ScreenshotDesignDto) -> Unit, (Throwable) -> Unit) -> Unit,
    loadLatest: ((ScreenshotDesignDto) -> Unit, (Throwable) -> Unit) -> Unit,
): DesignAutosaveController {
    val latestSave by rememberUpdatedState(save)
    val latestLoad by rememberUpdatedState(loadLatest)
    val controller = remember(slug, platform) {
        DesignAutosaveController(slug, platform, initial, baseVersion, canWrite,
            { request, success, failure -> latestSave(request, success, failure) },
            { success, failure -> latestLoad(success, failure) })
    }
    SideEffect { controller.writable = canWrite }
    DisposableEffect(controller) {
        val detach = controller.attach()
        onDispose { detach() }
    }
    return controller
}

private fun nearestAnchor(target: Any?): HTMLAnchorElement? {
    var element = target as? Element
    while (element != null) {
        if (element is HTMLAnchorElement) return element
        element = element.parentElement
    }
    return null
}

private fun designSaveIssues(error: Throwable): List<String> {
    val api = error as? ApiException ?: return listOf(tr("app.projects.slug.screenshots.design_autosave.couldnt_save_this_design_check_your", "Couldn't save this design. Check your connection and retry."))
    val issues = api.details["issues"] as? JsonArray
    return issues?.take(20)?.mapNotNull { issue ->
        val item = issue as? JsonObject ?: return@mapNotNull null
        val path = designPath(item["path"]?.jsonPrimitive?.content ?: "design")
        val message = item["message"]?.jsonPrimitive?.content?.let { tr("screenshots.design_validation.${item["code"]?.jsonPrimitive?.content ?: "invalid"}", it) } ?: tr("app.projects.slug.screenshots.design_autosave.check_this_part_of_the_design", "Check this part of the design.")
        trf("screenshots.design_autosave.repair_issue", "{path}: {message}", "path" to path, "message" to message)
    }?.takeIf { it.isNotEmpty() } ?: listOf(when (api.code) {
        "design_version_conflict" -> tr("app.projects.slug.screenshots.design_autosave.another_person_saved_a_newer_version", "Another person saved a newer version. Choose which design to keep.")
        "design_schema_unsupported" -> tr("app.projects.slug.screenshots.design_autosave.this_design_needs_a_newer_studio", "This design needs a newer studio. Reload before continuing.")
        "design_too_large" -> tr("app.projects.slug.screenshots.design_autosave.this_design_is_too_large_remove", "This design is too large. Remove some layers and retry.")
        else -> tr("app.projects.slug.screenshots.design_autosave.couldnt_save_this_design_retry", "Couldn't save this design. Retry.")
    })
}

private fun designPath(pointer: String): String = pointer.split('/').filter(String::isNotBlank)
    .joinToString(" · ") { part -> part.replace(Regex("([a-z])([A-Z])"), "$1 $2").lowercase() }.ifBlank { "design" }
